import {
  Truck,
  HardHat,
  Phone,
  ArrowUpRight,
  Construction,
  Hammer,
  PackageOpen,
  Mountain,
  Container,
  Wrench,
  Pickaxe,
  Pipette,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { COMPANY } from "../lib/company";

const AKERI_SERVICES = [
  {
    icon: Container,
    title: "Containertransport",
    desc: "Utsättning och hämtning av containrar för bygg- och rivningsavfall.",
  },
  {
    icon: PackageOpen,
    title: "Bulk & styckegods",
    desc: "Säker frakt av maskiner, byggmaterial och styckegods med kranbil.",
  },
  {
    icon: Wrench,
    title: "Kranbilstransport",
    desc: "Avlastning och placering med fordonsmonterad kran — perfekt på trånga byggarbetsplatser.",
  },
  {
    icon: Mountain,
    title: "Anläggningsmaterial",
    desc: "Leverans direkt från täkt till byggarbetsplats — grus, sand, jord, makadam.",
  },
  {
    icon: Trash2,
    title: "Byggavfallshantering",
    desc: "Upphämtning av allsköns säckar — vi tar hand om bygg- och rivningsavfall snabbt och säkert.",
  },
  {
    icon: AlertTriangle,
    title: "Farligt Avfallshämtning",
    desc: "Hämtning av asbest, tryckimpregnerat virke, gamla vitvaror och annat farligt avfall enligt regelverk.",
  },
];

const ENTREPRENAD_SERVICES = [
  {
    icon: HardHat,
    title: "Schakt & markarbeten",
    desc: "Schaktning för bostäder, kommersiella fastigheter, vägar och anläggningar.",
  },
  {
    icon: Pickaxe,
    title: "Grundläggning",
    desc: "Grundläggning för villor och industribyggnader — från utgrävning till färdig platta.",
  },
  {
    icon: Hammer,
    title: "Rivning & sanering",
    desc: "Kontrollerad rivning av byggnader och hårdgjorda ytor med fokus på återvinning.",
  },
  {
    icon: Pipette,
    title: "VA & ledningsarbeten",
    desc: "Förläggning av vatten, avlopp, dagvatten och el- och teleledningar.",
  },
  {
    icon: Construction,
    title: "Finplanering",
    desc: "Markplanering, plattläggning, asfaltsförberedelser och färdigställning av tomtmark.",
  },
  {
    icon: Mountain,
    title: "Snöröjning & sandning",
    desc: "Vinterunderhåll åt fastighetsägare och bostadsrättsföreningar i Haningeområdet.",
  },
];

function ServiceGrid({ services, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {services.map((s, i) => {
        const Icon = s.icon;
        return (
          <div
            key={i}
            className="bg-white border-2 border-black p-7 hover:bg-[#FFD700] transition-colors group"
            data-testid={`${testIdPrefix}-service-${i}`}
          >
            <div className="w-12 h-12 bg-black flex items-center justify-center mb-5 group-hover:bg-black">
              <Icon size={22} color="#FFD700" strokeWidth={2.5} />
            </div>
            <h3 className="font-display font-bold text-xl mb-2 text-black">
              {s.title}
            </h3>
            <p className="text-gray-700 leading-relaxed group-hover:text-black">
              {s.desc}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default function Tjanster() {
  return (
    <div data-testid="page-tjanster">
      {/* Page Header */}
      <section
        className="bg-black text-white py-20 lg:py-28 relative overflow-hidden"
        data-testid="tjanster-header"
      >
        <div className="hero-grain" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-[#FFD700] mb-5">Våra tjänster</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Från första schakt
            <br />
            till{" "}
            <span className="text-[#FFD700]">levererat material.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl leading-relaxed">
            Två kompletterande verksamheter under samma tak. Anlita en del
            eller hela kedjan.
          </p>
        </div>
      </section>

      {/* Åkeri */}
      <section
        className="bg-white py-20 lg:py-28"
        data-testid="akeri-services-section"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-[#FFD700] border-2 border-black px-3 py-1 font-condensed uppercase tracking-wider text-sm font-bold">
                  Verksamhet 01
                </span>
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight">
                Humle Åkeri AB
              </h2>
              <p className="mt-4 text-gray-600 text-lg max-w-2xl">
                Transport av tunga material och anläggningsgods med egen
                fordonsflotta.
              </p>
            </div>
          </div>
          <ServiceGrid services={AKERI_SERVICES} testIdPrefix="akeri" />
        </div>
      </section>

      {/* Divider */}
      <div className="diagonal-stripes h-3 w-full" />

      {/* Entreprenad */}
      <section
        className="bg-[#F3F4F6] py-20 lg:py-28"
        data-testid="entreprenad-services-section"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-black text-[#FFD700] border-2 border-black px-3 py-1 font-condensed uppercase tracking-wider text-sm font-bold">
                  Verksamhet 02
                </span>
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight">
                Humle Entreprenad AB
              </h2>
              <p className="mt-4 text-gray-600 text-lg max-w-2xl">
                Schakt, mark, anläggning och rivning med tunga maskiner och
                erfarna operatörer.
              </p>
            </div>
          </div>
          <ServiceGrid
            services={ENTREPRENAD_SERVICES}
            testIdPrefix="entreprenad"
          />
        </div>
      </section>

      {/* CTA */}
      <section
        className="bg-[#FFD700] py-20 lg:py-28 border-t-2 border-black"
        data-testid="tjanster-cta"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 text-center">
          <p className="overline text-black mb-5">Redo att starta?</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl text-black leading-[1] tracking-tight max-w-4xl mx-auto">
            Behöver du hjälp med ditt nästa projekt?
          </h2>
          <p className="mt-6 text-black/80 text-lg max-w-2xl mx-auto">
            Ring oss för en kostnadsfri offert. Vi svarar snabbt och rakt.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-3 bg-black text-[#FFD700] font-bold px-8 py-5 border-2 border-black hover:bg-gray-900 transition-colors"
              data-testid="tjanster-cta-phone"
            >
              <Phone size={22} strokeWidth={2.5} />
              <span className="font-display font-black text-xl lg:text-2xl">
                {COMPANY.phone}
              </span>
            </a>
            <a
              href={COMPANY.emailHref}
              className="inline-flex items-center gap-3 bg-transparent text-black font-bold px-8 py-5 border-2 border-black hover:bg-black hover:text-[#FFD700] transition-colors"
              data-testid="tjanster-cta-email"
            >
              <span className="font-condensed uppercase tracking-wider text-base">
                {COMPANY.email}
              </span>
              <ArrowUpRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
